// react core
import { useContext, useState } from "react";

// material design
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";

import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import NativeSelect from "@mui/material/NativeSelect";

// fonts and icons
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImageLandscape as iconImageFallback } from "@fortawesome/pro-thin-svg-icons";

// entzy config and context
import configEntzy from "components/config/ConfigEntzy";
import InputForm from "components/input/InputForm";
import { MainContext } from "components/main/MainContext";
import { EventContext } from "components/event/EventContext";
import { ActionButton } from "components/utils/common/CommonButtons";
import { ActionLoader } from "components/utils/common/CommonLoaders";
import { ActiveTag } from "components/utils/common/CommonTags";
import { timeout, getAge, dataUrlToFile, jsonTryParse } from "services/tools";

// entzy widgets and services
import { serviceObjectImageState } from "services/storage/object";
import { serviceObjectUpload } from "services/storage/object";

// entzy hooks
import { useMediaImage } from "hooks/media/mediaImage";

function ActionLaunchpadFrame(props) {
  const user = props.user;
  const handleEdit = props.handleEdit;
  const handleScrollTop = props.handleScrollTop;

  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);

  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState("");
  const [downloadedSelection, setDownloadedSelection] = useState([]);

  // capture current frame details;
  const frameId = props.frame.id;
  const frameCustom = props.frame.tag === "frameCustom";
  const frameName = frameCustom
    ? props.frame.name
    : frameId.replace("frame", "Frame ");
  const formFrameId =
    "event-" + eventContext.state.event.data.EventId + "-image-" + frameId;
  const frameData = eventContext.state.event.data.ImageFrames
    ? eventContext.state.event.data.ImageFrames.filter(
        (obj) => obj.id === frameId,
      )
    : [];
  const framePath = frameData.length
    ? frameData[0].path
    : configEntzy.EMPTY_STRING_SHORTSET;
  const frameMultiple = props.frame.multiple || false;
  const frameFromMaps = props.frame.fromMaps || false;

  // populate image frames onto latest hangers if exist
  const imageFrames = eventContext.hangImageFrames();
  const standardFrames = imageFrames.filter(
    (frame) => frame.tag !== "frameCustom",
  );

  // filter any downloaded images for the event when loading from maps
  const eventDownloadedImagesPlaces = frameFromMaps
    ? eventContext.state.downloadedImages.places.data
        .filter((obj) => obj.EventId === eventContext.state.event.data.EventId)
        .map((obj) => {
          return {
            id: obj.ImageId,
            locationId: obj.LinkId,
            content: obj.ImageContent,
            attributions: obj.ImageAttributions,
          };
        })
    : [];

  // filter any map locations to prevent api response overload with images
  const maxLaunchPlaces = 5;
  const clonedLaunchPlaces = [...eventContext.state.places.data.runners];
  const sortedLaunchPlaces = clonedLaunchPlaces.sort((a, b) => {
    const titleA = a.Title || "";
    const titleB = b.Title || "";
    const orderA = titleA.match(/\|\|(.*?)\|\|$/);
    const orderB = titleB.match(/\|\|(.*?)\|\|$/);
    const numA = orderA ? parseInt(orderA[1], 10) : Number.MAX_SAFE_INTEGER;
    const numB = orderB ? parseInt(orderB[1], 10) : Number.MAX_SAFE_INTEGER;
    return numA - numB;
  });
  const filteredLaunchPlaces = frameFromMaps
    ? sortedLaunchPlaces.length > maxLaunchPlaces
      ? sortedLaunchPlaces.slice(0, maxLaunchPlaces)
      : sortedLaunchPlaces
    : [];

  const handleImageFrame = async (data) => {
    if (eventContext.state.event.manager) {
      const uploadedValue =
        data === configEntzy.EMPTY_STRING_SHORTSET
          ? { key: configEntzy.EMPTY_STRING_SHORTSET }
          : data.filter((obj) => obj.id === formFrameId)[0].value;
      const uploadedImages = Array.isArray(uploadedValue)
        ? uploadedValue
        : [uploadedValue];

      // validate all images uploaded
      let allValid = true;
      for (let i = 0; i < uploadedImages.length; i++) {
        const img = uploadedImages[i];
        if (!img || !img.key || !img.key.length) {
          allValid = false;
          break;
        }
      }
      if (!allValid) {
        return {
          alert: true,
          message: "All images must be uploaded before proceeding",
        };
      }

      // handle multiple images and process all in parallel
      setLoading(true);
      const processImagePromises = uploadedImages.map(
        async (uploadedImage, index) => {
          let imageId = uploadedImage.selectedFrame
            ? uploadedImage.selectedFrame
            : frameId === "frame"
            ? `frame${index + 1}`
            : frameId;
          let count = 0,
            ready = false;
          if (uploadedImage.key === configEntzy.EMPTY_STRING_SHORTSET) {
            // this is a remove image request
            uploadedImage.preload = null;
            ready = true;
          } else {
            // for new image first check all resized images are available
            do {
              await timeout(count * 1000);
              ready = await serviceObjectImageState(uploadedImage.key);
              count++;
            } while (count < 10 && !ready);
          }
          return {
            id: imageId,
            uploadedImage,
            ready,
            description: uploadedImage.description,
          };
        },
      );

      const processedImages = await Promise.all(processImagePromises);
      let allProcessedValid = true;
      processedImages.forEach(({ uploadedImage, ready }) => {
        if (!ready) {
          allProcessedValid = false;
        }
      });

      if (allProcessedValid) {
        processedImages.forEach(({ id, uploadedImage }) => {
          imageFrames.forEach((frame) => {
            if (frame.id === id) {
              frame.path = uploadedImage.key;
              frame.active =
                uploadedImage.key === configEntzy.EMPTY_STRING_SHORTSET
                  ? false
                  : true;
              frame.description = uploadedImage.description; // optional
            }
          });
        });

        const eventUpdate = await eventContext.prepareUpdateEvent({
          key: "ImageFrames",
          value: imageFrames,
          // preload: uploadedImage.preload,
        });
        setLoading(false);
        setSelection("");
        if (eventUpdate.alert) {
          return {
            alert: true,
            message: eventUpdate.message,
          };
        } else {
          eventContext.updateEvent(eventUpdate);
          handleEdit(null, frameId);
          return { alert: false };
        }
      } else {
        setLoading(false);
        setSelection("");
        return {
          alert: true,
          message:
            "One or more images could not be processed. Give it another go or if this problem persists try another image size or format.",
        };
      }
    } else {
      return {
        alert: true,
        message: "Eventuator runner permissions required for this update",
      };
    }
  };

  const handleLoadImagesFromPlaces = async () => {
    setLoading(true);
    const response = await eventContext.prepareGeneratePlaceImages({
      imageType: "photo",
    });
    setLoading(false);
    if (response.alert) {
      mainContext.updateAlert(response);
      return response;
    }
    eventContext.generatePlaceImages(response);
    return response;
  };

  const handleTransferImagesToFrames = async () => {
    if (!downloadedSelection.length) {
      mainContext.updateAlert({
        alert: true,
        message:
          "No images selected. Select one or more downloaded images to transfer to your frames.",
      });
      return;
    }
    if (downloadedSelection.length > standardFrames.length) {
      mainContext.updateAlert({
        alert: true,
        message:
          "You have selected more images than available frames. Reduce your selection and give it another go.",
      });
      return;
    }

    handleScrollTop();
    setLoading(true);

    const uploadPromises = downloadedSelection.map(async (selectedImage) => {
      const image = eventDownloadedImagesPlaces.filter(
        (img) => img.id === selectedImage.id,
      )[0];
      const selectedFrame = selectedImage.selectedFrameId;
      const fileKey =
        formFrameId.replace(`image-${frameId}`, `image-${selectedFrame}`) +
        "-" +
        Date.now();
      const fileBlob = dataUrlToFile(image.content, fileKey);
      // const fileSize = fileBlob.size;
      const fileUpload = await serviceObjectUpload(
        "images",
        "public",
        fileKey,
        fileBlob,
        user.identity,
      );
      if (!fileUpload.success) {
        setLoading(false);
        mainContext.updateAlert({
          alert: true,
          message: "Image copy upload failed. Give it another go.",
        });
        return;
      }
      return {
        key: fileUpload.data.key,
        selectedFrame,
        fileKey,
        attributions: image.attributions,
      };
    });
    const uploadedImages = await Promise.all(uploadPromises);

    // check all uploads successful
    let allUploadsSuccessful = true;
    uploadedImages.forEach((obj) => {
      if (!obj || !obj.key || !obj.key.length) {
        allUploadsSuccessful = false;
      }
    });
    if (!allUploadsSuccessful) {
      setLoading(false);
      mainContext.updateAlert({
        alert: true,
        message:
          "One or more image transfers failed. Give it another go or try another selection if the problem persists.",
      });
      return;
    }

    // update event frames with new image keys
    await handleImageFrame([
      {
        id: formFrameId,
        value: uploadedImages.map((obj) => {
          // parse and format attributions if exist
          let attributions = {};
          if (obj.attributions) {
            attributions = jsonTryParse(obj.attributions, []);
            attributions = attributions.map((attr) => {
              return {
                userName: attr.displayName || "",
                userUrl: attr.uri || "",
                photoUrl: attr.photoUri || "",
              };
            });
          }
          // add attributions to image description
          let description = {};
          description.attributions = attributions;
          description.source = "Google Places";
          return {
            key: obj.key,
            preload: null,
            selectedId: obj.fileKey,
            selectedFrame: obj.selectedFrame,
            description: JSON.stringify(description),
          };
        }),
      },
    ]);

    setLoading(false);
  };

  // load page images
  const mediaImage = useMediaImage(
    eventContext.state.event.hydrated
      ? {
          user: user,
          context: mainContext,
          image: {
            id:
              "event:frames:" +
              frameId +
              ":" +
              eventContext.state.event.data.EventId,
            key: framePath,
          },
        }
      : null,
  );

  return (
    <Box className="box-default">
      <Box className="box-default" hidden={frameFromMaps}>
        {loading || !mediaImage ? (
          <Box
            className="box-default"
            sx={{
              p: configEntzy.APP_SPACING_SM,
              mb: configEntzy.APP_SPACING_MD2X,
            }}
          >
            <Box
              className="box-default bg-black-t50 shadow-darkest"
              sx={{
                pt: configEntzy.APP_SPACING_XL,
                pb: configEntzy.APP_SPACING_XL,
                borderRadius: configEntzy.BORDER_SIZE_XL,
              }}
            >
              <ActionLoader size="md" color="success" />
            </Box>
          </Box>
        ) : (
          <Box
            className="box-default"
            sx={{ mb: configEntzy.APP_SPACING_MD }}
            hidden={selection && selection !== "logo"}
          >
            {framePath !== configEntzy.EMPTY_STRING_SHORTSET ? (
              <Box
                className="box-default bg-black-t50 shadow-dark"
                sx={{
                  p: configEntzy.APP_SPACING_MD,
                  borderRadius: configEntzy.BORDER_SIZE_XL,
                }}
              >
                <Box className="box-default relative">
                  <Box
                    className="box-default"
                    sx={{
                      "&::before": {
                        content: `''`,
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundImage:
                          mediaImage.hydrated && mediaImage.images.lg
                            ? "url(" + mediaImage.images.lg.data + ")"
                            : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "repeat-y",
                        // filter:
                        //   mediaImage.hydrated && mediaImage.images.lg
                        //     ? "blur(1px)"
                        //     : "none",
                        opacity: 1,
                        zIndex: -1,
                      },
                    }}
                  >
                    <Box
                      className="box-default"
                      sx={{
                        pt: configEntzy.APP_SPACING_XL,
                        pb: configEntzy.APP_SPACING_XL,
                      }}
                    >
                      <Box className="box-default">
                        <ActiveTag prefix={frameName} value={true} />
                      </Box>
                      <Box
                        className="box-default"
                        sx={{
                          mt: configEntzy.APP_SPACING_MD,
                        }}
                      >
                        <ActionButton
                          size="small"
                          text="Remove"
                          color="danger"
                          onClick={() =>
                            handleImageFrame(configEntzy.EMPTY_STRING_SHORTSET)
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box className="box-default">
                <Collapse
                  in={true}
                  timeout={{
                    enter: configEntzy.TRANSITIONS.default,
                    exit: configEntzy.TRANSITIONS.default,
                  }}
                >
                  <Box
                    className="box-default"
                    sx={{ mt: configEntzy.APP_SPACING_MD }}
                  >
                    <InputForm
                      user={user}
                      navigate={props.navigate}
                      fields={[
                        {
                          id: formFrameId,
                          type: "image-picker",
                          level: "public",
                          value: "",
                          image:
                            framePath !== configEntzy.EMPTY_STRING_SHORTSET
                              ? framePath
                              : null,
                          required: true,
                          disablePadding: true,
                          multiple: frameMultiple,
                        },
                      ]}
                      submitText="All Good? Confirm Update"
                      submitType="flow"
                      openImagePicker={true}
                      fallbackImage={iconImageFallback}
                      callback={handleImageFrame}
                      confirmation={
                        "Image " + frameId + " updated successfully"
                      }
                      // cancel={handleShowCreate}
                      // cancelText="Cancel"
                      // footNotes={["Background logo for your eventuator"]}
                    />
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Box className="box-default" hidden={!frameFromMaps}>
        {loading ? (
          <Box
            className="box-default"
            sx={{
              p: configEntzy.APP_SPACING_SM,
              mb: configEntzy.APP_SPACING_MD2X,
            }}
          >
            <Box
              className="box-default bg-black-t50 shadow-darkest"
              sx={{
                pt: configEntzy.APP_SPACING_XL,
                pb: configEntzy.APP_SPACING_XL,
                borderRadius: configEntzy.BORDER_SIZE_XL,
              }}
            >
              <ActionLoader size="md" color="success" />
            </Box>
          </Box>
        ) : (
          <Box
            className="box-default"
            sx={{
              p: configEntzy.APP_SPACING_SM,
              mb: configEntzy.APP_SPACING_MD2X,
            }}
          >
            <Box
              className="box-default bg-black-t50 shadow-dark"
              sx={{
                p: configEntzy.APP_SPACING_MD,
                borderRadius: configEntzy.BORDER_SIZE_XL,
              }}
            >
              <Box
                className="box-default text-paragraph"
                sx={{
                  pt: configEntzy.APP_SPACING_MD,
                }}
              >
                <Typography variant="h6" color="primary">
                  Browse Google Places
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="white"
                  sx={{
                    mt: configEntzy.APP_SPACING_SM,
                  }}
                  hidden={eventDownloadedImagesPlaces.length > 0}
                >
                  Select images from google, linked to your eventuator launch
                  location(s)
                </Typography>
                {!filteredLaunchPlaces.length && (
                  <Typography
                    variant="subtitle2"
                    sx={{ mt: configEntzy.APP_SPACING_MD }}
                  >
                    There are no locations configured yet in your map settings.
                    Add one first to enable this feature.
                  </Typography>
                )}
              </Box>
              <Box
                className="box-default text-paragraph"
                sx={{
                  mt: configEntzy.APP_SPACING_MD2X,
                  mb: configEntzy.APP_SPACING_MD2X,
                  pt: configEntzy.APP_SPACING_MD,
                  pb: configEntzy.APP_SPACING_MD,
                  borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                <Typography variant="subtitle2" color="secondary">
                  <span>Active Launch Locations:</span>
                  <span>&nbsp;</span>
                  <span>{filteredLaunchPlaces.length}</span>
                </Typography>
                {eventContext.state.places.data.runners.length >
                  filteredLaunchPlaces.length && (
                  <Typography
                    variant="subtitle2"
                    color="error"
                    sx={{ mt: configEntzy.APP_SPACING_XS }}
                  >
                    <span>Places limited to top</span>
                    <span>&nbsp;</span>
                    {maxLaunchPlaces}
                    <span>&nbsp;</span>
                    <span>when downloading photos</span>
                  </Typography>
                )}
                {filteredLaunchPlaces.map((place, index) => {
                  let maxLength = 30;
                  let description = place.Description
                    ? place.Description
                    : "No description";
                  let title = place.Title ? place.Title : "No title";
                  description =
                    description.length > maxLength
                      ? description.substr(0, maxLength) + ".."
                      : description;
                  title = title.replace(/\|\|.*?\|\|/g, "").trim();
                  title =
                    title.length > maxLength
                      ? title.substr(0, maxLength) + ".."
                      : title;
                  return (
                    <Typography
                      key={index}
                      variant="subtitle2"
                      sx={{ mt: configEntzy.APP_SPACING_XS }}
                      noWrap={true}
                    >
                      <em>
                        <span>{description}</span>
                        <span>&nbsp; / &nbsp;</span>
                        <span>{title}</span>
                      </em>
                    </Typography>
                  );
                })}
              </Box>
              <Box
                className="box-default"
                sx={{ pb: configEntzy.APP_SPACING_MD2X }}
              >
                <ActionButton
                  size="small"
                  text={
                    eventDownloadedImagesPlaces.length > 0
                      ? "Reload Place Images"
                      : "Download Place Images"
                  }
                  color={
                    eventDownloadedImagesPlaces.length > 0 ? "night" : "primary"
                  }
                  onClick={handleLoadImagesFromPlaces}
                  disabled={!filteredLaunchPlaces.length || loading}
                  border={true}
                />
              </Box>
              {eventDownloadedImagesPlaces.length > 0 && (
                <Box className="box-default">
                  <Box
                    className="box-default text-paragraph"
                    sx={{
                      px: configEntzy.APP_SPACING_MD,
                      pb: configEntzy.APP_SPACING_MD,
                    }}
                  >
                    <Typography variant="subtitle2" color="secondary">
                      Your downloaded images are ready below. Select one or more
                      and transfer when ready.
                    </Typography>
                  </Box>

                  <Box className="box-default">
                    <ActionButton
                      size="small"
                      text={
                        "Transfer Selected to Gallery" +
                        (downloadedSelection.length
                          ? " (" + downloadedSelection.length + ")"
                          : "")
                      }
                      color="primary"
                      onClick={handleTransferImagesToFrames}
                      disabled={
                        !downloadedSelection.length ||
                        loading ||
                        downloadedSelection.length > standardFrames.length
                      }
                    />
                  </Box>
                </Box>
              )}

              {eventContext.state.downloadedImages.places.updated && (
                <Box className="box-default">
                  {eventDownloadedImagesPlaces.length > 0 && (
                    <Box className="box-default">
                      {filteredLaunchPlaces.map((location) => {
                        const images = eventDownloadedImagesPlaces.filter(
                          (img) => img.locationId === location.LinkId,
                        );
                        const description = location
                          ? location.Description
                          : "No description";
                        const title = location
                          ? location.Title.replace(/\|\|.*?\|\|/g, "").trim()
                          : "No title";

                        return (
                          <Box className="box-default" key={location.LinkId}>
                            <Box
                              className="box-default"
                              sx={{
                                mt: configEntzy.APP_SPACING_MD2X,
                                pt: configEntzy.APP_SPACING_MD2X,
                                borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                                color: "white",
                              }}
                            >
                              <Typography variant="subtitle2" noWrap={true}>
                                {description}
                              </Typography>
                              <Typography variant="subtitle2" noWrap={true}>
                                {title}
                              </Typography>
                              {images.length === 0 ? (
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    mt: configEntzy.APP_SPACING_MD,
                                    mb: configEntzy.APP_SPACING_MD3X,
                                    color: "gray",
                                  }}
                                >
                                  <em>No images found for this location</em>
                                </Typography>
                              ) : (
                                <Typography
                                  variant="subtitle1"
                                  color="secondary"
                                >
                                  {images.length} image
                                  {images.length > 1 ? "s" : ""} found
                                </Typography>
                              )}
                            </Box>
                            <Box
                              className="box-default"
                              sx={{
                                py: configEntzy.APP_SPACING_MD,
                              }}
                              hidden={images.length === 0}
                            >
                              {images.map((image, index) => {
                                return (
                                  <Box
                                    key={image.id}
                                    className="box-inline"
                                    sx={{
                                      width: {
                                        xs: 122,
                                        sm: 153,
                                        md: 184,
                                        lg: 205,
                                      },
                                    }}
                                  >
                                    <Box
                                      className="box-default action-pointer"
                                      sx={{
                                        width: {
                                          xs: 120,
                                          sm: 150,
                                          md: 180,
                                          lg: 200,
                                        },
                                        height: {
                                          xs: 120,
                                          sm: 150,
                                          md: 180,
                                          lg: 200,
                                        },
                                        backgroundImage:
                                          "url(" + image.content + ")",
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        borderRadius:
                                          configEntzy.BORDER_SIZE_MD,
                                        m: configEntzy.APP_SPACING_XS3X,
                                      }}
                                      onClick={() => {
                                        document
                                          .getElementById(
                                            "selected-download-" + image.id,
                                          )
                                          .click();
                                      }}
                                    >
                                      <Box
                                        className="box-inline left"
                                        sx={{
                                          p: configEntzy.APP_SPACING_SM,
                                        }}
                                      >
                                        <Box
                                          className="box-default bg-black-t50 shadow-darkest"
                                          sx={{
                                            borderRadius:
                                              configEntzy.BORDER_SIZE_XL,
                                          }}
                                        >
                                          <Checkbox
                                            id={"selected-download-" + image.id}
                                            checked={
                                              downloadedSelection.filter(
                                                (obj) => obj.id === image.id,
                                              ).length > 0
                                            }
                                            onChange={() => {
                                              let newSelection =
                                                downloadedSelection.filter(
                                                  (obj) => obj.id !== image.id,
                                                );
                                              if (
                                                downloadedSelection.filter(
                                                  (obj) => obj.id === image.id,
                                                ).length === 0
                                              ) {
                                                newSelection.push({
                                                  id: image.id,
                                                  locationId: image.locationId,
                                                  selectedFrameId:
                                                    document.getElementById(
                                                      "selected-frame-" +
                                                        image.id,
                                                    ).value,
                                                });
                                              }
                                              setDownloadedSelection(
                                                newSelection,
                                              );
                                            }}
                                            size="small"
                                            color="primary"
                                            sx={{
                                              p: configEntzy.APP_SPACING_SM,
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    </Box>
                                    <Box className="box-default">
                                      <FormControl fullWidth>
                                        <NativeSelect
                                          defaultValue={
                                            imageFrames[index]
                                              ? imageFrames[index].id
                                              : imageFrames[0].id
                                          }
                                          inputProps={{
                                            id: "selected-frame-" + image.id,
                                          }}
                                          sx={{
                                            pl: configEntzy.APP_SPACING_MD,
                                          }}
                                          onChange={(e) => {
                                            let newSelection =
                                              downloadedSelection.filter(
                                                (obj) => obj.id !== image.id,
                                              );
                                            newSelection.push({
                                              id: image.id,
                                              locationId: image.locationId,
                                              selectedFrameId: e.target.value,
                                            });
                                            setDownloadedSelection(
                                              newSelection,
                                            );
                                          }}
                                        >
                                          {imageFrames
                                            .filter((frame) => !frameCustom)
                                            .map((frame) => {
                                              return (
                                                <option
                                                  key={frame.id}
                                                  value={frame.id}
                                                >
                                                  {frame.id.replace(
                                                    "frame",
                                                    "Frame ",
                                                  )}
                                                </option>
                                              );
                                            })}
                                        </NativeSelect>
                                      </FormControl>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {eventDownloadedImagesPlaces.length > 0 && (
                    <Box
                      className="box-default"
                      sx={{ mt: configEntzy.APP_SPACING_MD2X }}
                    >
                      <ActionButton
                        size="small"
                        text={
                          "Transfer Selected to Gallery" +
                          (downloadedSelection.length
                            ? " (" + downloadedSelection.length + ")"
                            : "")
                        }
                        color="primary"
                        onClick={handleTransferImagesToFrames}
                        disabled={
                          !downloadedSelection.length ||
                          loading ||
                          downloadedSelection.length > standardFrames.length
                        }
                      />
                    </Box>
                  )}

                  <Box
                    className="box-default"
                    sx={{
                      mt: configEntzy.APP_SPACING_MD3X,
                      pt: configEntzy.APP_SPACING_MD,
                      borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    {eventDownloadedImagesPlaces.length === 0 && (
                      <Typography
                        variant="subtitle2"
                        className="text-paragraph"
                        sx={{ mb: configEntzy.APP_SPACING_MD }}
                      >
                        No images available from Google at the locations
                        configured. Try adding more locations or check your map.
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="secondary"
                      className="upper-case"
                    >
                      <span>Images Found</span>
                      <span>&nbsp;&nbsp;</span>
                      <span className="bg-black bdp2 rounded">
                        <span>&nbsp;</span>
                        {eventDownloadedImagesPlaces.length}
                        <span>&nbsp;</span>
                      </span>
                      <span>&nbsp;&nbsp;</span>
                      <span>Last Load</span>
                      <span>&nbsp;&nbsp;</span>
                      <span className="bg-black bdp2 rounded">
                        {getAge({
                          timestampObj:
                            eventContext.state.downloadedImages.places.updated,
                        })}
                        <span>&nbsp;</span>
                      </span>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default ActionLaunchpadFrame;
